// Billing API client — mirrors the backend billing contract exactly.
// Organisation (developer) + admin endpoints go through the shared axios client
// (cookie auth + automatic CSRF on mutations). Applicant endpoints are called
// with the hosted page's session token / API key, like the other
// /api/v2/verify/:id/* routes.

import { createApiClient, type ApiError } from './apiClient'
import { API_BASE_URL } from '../config/api'

const api = createApiClient(`${API_BASE_URL}/api`)

// ─── Types ──────────────────────────────────────────────────────────

export type TransactionType = 'topup' | 'verification' | 'applicant_fee' | 'refund' | 'adjustment'

export interface ApplicantFeeSettings {
  enabled: boolean
  amount_cents: number
  description: string | null
}

export interface WalletMonth {
  month: string // 'YYYY-MM'
  spent_cents: number
  topup_cents: number
  applicant_fee_cents: number
  verifications: number
}

export interface Wallet {
  enabled: boolean
  currency: 'USD'
  balance_cents: number
  price_per_verification_cents: number
  low_balance: boolean
  min_topup_cents: number
  applicant_fee: ApplicantFeeSettings
  stats: {
    spent_this_month_cents: number
    verifications_this_month: number
    applicant_fees_this_month_cents: number
    topups_this_month_cents: number
  }
  monthly: WalletMonth[]
}

export interface BillingTransaction {
  id: string
  type: TransactionType
  amount_cents: number
  balance_after_cents: number
  description: string | null
  reference: string | null
  verification_id: string | null
  created_at: string
}

export interface AdminBillingTransaction extends BillingTransaction {
  developer_id: string
  developer_name: string | null
  developer_email: string | null
}

export interface Paginated<T> { items: T[]; total: number }

export interface BillingPayment {
  id: string
  tx_ref: string
  payer_type: 'org' | 'applicant'
  payer_name: string | null
  payer_email: string | null
  amount_cents: number
  currency: string
  status: 'pending' | 'successful' | 'failed'
  verification_id: string | null
  created_at: string
}

export interface CheckoutSession { checkout_url: string; tx_ref: string }

export interface ApplicantPaymentInfo {
  required: boolean
  paid: boolean
  amount_cents: number
  currency: string
  description: string | null
  organisation_name: string | null
}

export interface AdminBillingOverview {
  enabled: boolean
  currency: 'USD'
  totals: {
    revenue_cents: number
    topups_cents: number
    applicant_fees_cents: number
    verification_charges_cents: number
    outstanding_wallet_balance_cents: number
    paying_orgs: number
    verifications_charged: number
  }
  monthly: Array<{ month: string; topups_cents: number; applicant_fees_cents: number; verification_charges_cents: number }>
  top_orgs: Array<{ developer_id: string; name: string | null; email: string | null; balance_cents: number; spent_cents: number }>
  settings: { price_per_verification_cents: number }
}

export interface AdminWallet {
  developer_id: string
  name: string | null
  email: string | null
  company: string | null
  balance_cents: number
  price_override_cents: number | null
  applicant_fee_enabled: boolean
  updated_at: string | null
}

// ─── Error helpers ──────────────────────────────────────────────────

/** Human message for a billing error (ApiError from the axios client or anything thrown). */
export function billingErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  const e = err as Partial<ApiError> | undefined
  switch (e?.code) {
    case 'payments_not_configured':
      return 'Online payments are not configured on this server yet.'
    case 'payment_provider_error':
      return 'The payment provider is not responding. Please try again in a moment.'
    case 'invalid_amount': {
      const min = e?.data?.min_topup_cents, max = e?.data?.max_topup_cents
      return typeof min === 'number' && typeof max === 'number'
        ? `Enter an amount between $${(min / 100).toFixed(2)} and $${(max / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}.`
        : e?.data?.message || 'That amount is not allowed.'
    }
    case 'insufficient_balance':
      // initialize → 402; admin adjust → 400 (debit larger than the balance)
      if (e?.status === 400) {
        const bal = e?.data?.balance_cents
        return typeof bal === 'number' ? `That debit is larger than the wallet balance ($${(bal / 100).toFixed(2)}). Wallets can't go negative.` : "That debit is larger than the wallet balance. Wallets can't go negative."
      }
      return 'Your wallet balance is too low to start a live verification.'
    case 'payment_required':
      return 'This verification requires a fee to be paid first.'
    case 'payment_not_required':
      return 'No fee is due for this verification.'
    case 'not_found':
      return e?.data?.message || 'That organisation could not be found.'
    case 'validation_error':
      return e?.data?.message || e?.message || 'Please check the values and try again.'
    case 'billing_disabled':
      return 'Billing is not enabled on this server.'
  }
  if (e?.message && e.message !== 'An unexpected error occurred') return e.message
  if (e?.data?.message) return e.data.message
  return fallback
}

const qs = (params: Record<string, string | number | undefined | null>) => {
  const p = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.set(k, String(v)) })
  const s = p.toString()
  return s ? `?${s}` : ''
}

// ─── Organisation (developer) ───────────────────────────────────────

export async function getWallet(): Promise<Wallet> {
  const { data } = await api.get<Wallet>('/developer/billing/wallet')
  return data
}

export async function getTransactions(opts: { limit?: number; offset?: number; type?: TransactionType | '' } = {}): Promise<Paginated<BillingTransaction>> {
  const { data } = await api.get(`/developer/billing/transactions${qs({ limit: opts.limit ?? 20, offset: opts.offset ?? 0, type: opts.type })}`)
  return { items: data.transactions ?? [], total: data.total ?? 0 }
}

export async function createTopup(amountCents: number): Promise<CheckoutSession> {
  const { data } = await api.post<CheckoutSession>('/developer/billing/topup', { amount_cents: amountCents })
  return data
}

export async function updateApplicantFee(body: ApplicantFeeSettings): Promise<ApplicantFeeSettings> {
  const { data } = await api.put<any>('/developer/billing/applicant-fee', body)
  return (data?.applicant_fee ?? data) as ApplicantFeeSettings
}

export async function getPayments(opts: { limit?: number; offset?: number; payer_type?: 'org' | 'applicant' } = {}): Promise<Paginated<BillingPayment>> {
  const { data } = await api.get(`/developer/billing/payments${qs({ limit: opts.limit ?? 20, offset: opts.offset ?? 0, payer_type: opts.payer_type })}`)
  return { items: data.payments ?? [], total: data.total ?? 0 }
}

// ─── Applicant (hosted verification page) ───────────────────────────

export interface ApplicantAuth { sessionToken?: string; apiKey?: string }

const applicantHeaders = (auth: ApplicantAuth): Record<string, string> =>
  auth.sessionToken ? { 'X-Session-Token': auth.sessionToken } : auth.apiKey ? { 'X-API-Key': auth.apiKey } : {}

export class ApplicantPaymentError extends Error {
  constructor(message: string, public status: number, public code?: string) { super(message) }
}

export async function getApplicantPayment(verificationId: string, auth: ApplicantAuth): Promise<ApplicantPaymentInfo> {
  const res = await fetch(`${API_BASE_URL}/api/v2/verify/${encodeURIComponent(verificationId)}/payment`, {
    headers: applicantHeaders(auth),
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new ApplicantPaymentError(body?.message || `HTTP ${res.status}`, res.status, body?.error)
  return body as ApplicantPaymentInfo
}

export async function startApplicantPayment(
  verificationId: string,
  auth: ApplicantAuth,
  payer: { name: string; email: string; phone?: string },
  /** Where the callback should send the applicant back to (defaults to the current URL, keeping ?session=…). */
  returnUrl: string = typeof window !== 'undefined' ? window.location.href : '',
): Promise<CheckoutSession> {
  const res = await fetch(`${API_BASE_URL}/api/v2/verify/${encodeURIComponent(verificationId)}/payment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...applicantHeaders(auth) },
    body: JSON.stringify({
      name: payer.name.trim(),
      email: payer.email.trim(),
      ...(payer.phone?.trim() ? { phone: payer.phone.trim() } : {}),
      ...(returnUrl ? { return_url: returnUrl } : {}),
    }),
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = body?.error === 'payments_not_configured' || body?.error === 'payment_provider_error'
      ? 'Online payments are temporarily unavailable. Please try again in a moment.'
      : body?.message || 'Could not start the payment. Please try again.'
    throw new ApplicantPaymentError(msg, res.status, body?.error)
  }
  return body as CheckoutSession
}

// ─── Admin ──────────────────────────────────────────────────────────

export async function getAdminOverview(): Promise<AdminBillingOverview> {
  const { data } = await api.get<AdminBillingOverview>('/admin/billing/overview')
  return data
}

export async function getAdminTransactions(opts: { limit?: number; offset?: number; developer_id?: string; type?: TransactionType | '' } = {}): Promise<Paginated<AdminBillingTransaction>> {
  const { data } = await api.get(`/admin/billing/transactions${qs({ limit: opts.limit ?? 20, offset: opts.offset ?? 0, developer_id: opts.developer_id, type: opts.type })}`)
  return { items: data.transactions ?? [], total: data.total ?? 0 }
}

export async function getAdminWallets(opts: { limit?: number; offset?: number; search?: string } = {}): Promise<Paginated<AdminWallet>> {
  const { data } = await api.get(`/admin/billing/wallets${qs({ limit: opts.limit ?? 20, offset: opts.offset ?? 0, search: opts.search })}`)
  return { items: data.wallets ?? [], total: data.total ?? 0 }
}

export async function updateAdminSettings(pricePerVerificationCents: number): Promise<{ price_per_verification_cents: number }> {
  const { data } = await api.put('/admin/billing/settings', { price_per_verification_cents: pricePerVerificationCents })
  return data
}

export async function updateWalletPriceOverride(developerId: string, priceOverrideCents: number | null): Promise<AdminWallet> {
  const { data } = await api.put(`/admin/billing/wallets/${encodeURIComponent(developerId)}`, { price_override_cents: priceOverrideCents })
  return data as AdminWallet
}

export async function adjustWallet(developerId: string, amountCents: number, reason: string): Promise<AdminBillingTransaction> {
  const { data } = await api.post(`/admin/billing/wallets/${encodeURIComponent(developerId)}/adjust`, { amount_cents: amountCents, reason })
  return (data?.transaction ?? data) as AdminBillingTransaction
}
